import { useEffect, useRef, useState } from 'react';
import { navigate } from '../router';
import { hhmm } from '../sportcenter';
import { hubApi, type HubChannel, type HubPulse } from '../sporthub';
import { playLink } from './LiveBits';

/**
 * Sport op de achtergrond (zoals IPTV-apps met een sporthub): een melding als een club die je volgt scoort,
 * een herinnering 15 minuten voor de aftrap, en 1 minuut ervoor vanzelf naar de wedstrijd (als je dat aanzette).
 * Werkt overal in de app, ook terwijl je iets anders kijkt.
 */

interface Toast { id: string; kind: 'goal' | 'remind' | 'switch'; title: string; text: string; channel: HubChannel | null; at: number; deadline?: number }

export function SportWatcher({ enabled }: { enabled: boolean }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const scores = useRef(new Map<string, string>());
  const done = useRef(new Set<string>());
  const pulse = useRef<HubPulse | null>(null);

  // Ophalen: elke 30-60 s (de server houdt het licht met een gedeelde cache)
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let timer: number | undefined;
    const load = async () => {
      const p = await hubApi.pulse().catch(() => null);
      if (!alive) return;
      if (p) {
        pulse.current = p;
        const fresh: Toast[] = [];
        for (const m of p.live) {
          const now = `${m.home.score ?? 0}-${m.away.score ?? 0}`;
          const before = scores.current.get(m.key);
          scores.current.set(m.key, now);
          if (!before || before === now || !m.goal || p.hideScores) continue;
          const [bh, ba] = before.split('-').map(Number);
          const scorer = Number(m.home.score) > bh ? m.home.short : Number(m.away.score) > ba ? m.away.short : null;
          if (!scorer) continue;
          fresh.push({ id: `g:${m.key}:${now}`, kind: 'goal', title: `DOELPUNT ${scorer}`, text: `${m.home.short} ${m.home.score}–${m.away.score} ${m.away.short}${m.minute ? ` · ${m.minute}` : ''}`, channel: m.channel, at: Date.now() });
        }
        if (fresh.length) setToasts((t) => [...t, ...fresh].slice(-4));
      }
      timer = window.setTimeout(load, (p?.poll ?? 60) * 1000);
    };
    void load();
    return () => { alive = false; window.clearTimeout(timer); };
  }, [enabled]);

  // Herinneringen en overschakelen: elke 5 s kijken of het tijd is
  useEffect(() => {
    if (!enabled) return;
    const t = window.setInterval(() => {
      const now = Date.now();
      for (const u of pulse.current?.upcoming ?? []) {
        const left = u.start - now;
        if (left < 15 * 60_000 && left > 2 * 60_000 && !done.current.has(`r:${u.key}`)) {
          done.current.add(`r:${u.key}`);
          setToasts((x) => [...x, { id: `r:${u.key}`, kind: 'remind', title: u.title, text: `Begint om ${hhmm(u.start)}${u.channel ? ` op ${u.channel.name}` : ''}`, channel: u.channel, at: now }]);
        }
        if (u.auto && u.channel && left < 70_000 && left > -5 * 60_000 && !done.current.has(`s:${u.key}`)) {
          done.current.add(`s:${u.key}`);
          setToasts((x) => [...x.filter((y) => y.id !== `r:${u.key}`), { id: `s:${u.key}`, kind: 'switch', title: u.title, text: `Begint zo · over 10 s naar ${u.channel!.name}`, channel: u.channel, at: now, deadline: now + 10_000 }]);
        }
      }
      // Overschakelen als de tijd om is (en niemand "Niet overschakelen" koos)
      setToasts((x) => {
        const due = x.find((y) => y.kind === 'switch' && y.deadline && y.deadline <= now);
        if (due?.channel) {
          navigate(playLink(due.channel.accountId, due.channel.streamId));
          return x.filter((y) => y.id !== due.id);
        }
        // Meldingen verdwijnen vanzelf
        return x.filter((y) => y.kind === 'switch' || now - y.at < (y.kind === 'goal' ? 12_000 : 60_000));
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, [enabled]);

  if (!toasts.length) return null;
  const close = (id: string) => setToasts((x) => x.filter((y) => y.id !== id));
  return (
    <div className="toasts sh-toasts" role="region" aria-label="Sport" aria-live="assertive">
      {toasts.map((t) => (
        <div key={t.id} className={`toast sh-toast sh-toast-${t.kind}`}>
          {t.kind === 'goal' && <span className="sh-toast-tag">DOELPUNT</span>}
          <strong>{t.kind === 'goal' ? t.title.replace(/^DOELPUNT /, '') : t.title}</strong>
          <span className="muted">{t.kind === 'switch' && t.deadline ? `Over ${Math.max(0, Math.ceil((t.deadline - Date.now()) / 1000))} s naar ${t.channel?.name}` : t.text}</span>
          <div className="form-actions">
            {t.channel && <button className="btn btn-primary btn-sm" onClick={() => { close(t.id); navigate(playLink(t.channel!.accountId, t.channel!.streamId)); }}>{t.kind === 'switch' ? 'Nu kijken' : 'Kijken'}</button>}
            <button className="btn btn-sm" onClick={() => close(t.id)}>{t.kind === 'switch' ? 'Niet overschakelen' : 'Sluiten'}</button>
          </div>
        </div>
      ))}
    </div>
  );
}
